import { ADD_ON_MODULES, MODULE_CATALOGUE, MODULE_CODES } from './modules';
import { PERMISSIONS } from './permissions';
import { ROLE_TEMPLATES } from './roles';
const d = (code, name, kind) => ({ code, name, kind });
const CLINICAL_DEPARTMENTS = [
    d('GEN', 'General Medicine', 'clinical'),
    d('FAM', 'Family Medicine / GP', 'clinical'),
    d('SUR', 'General Surgery', 'clinical'),
    d('GYN', 'Gynaecology & Obstetrics', 'clinical'),
    d('PED', 'Paediatrics', 'clinical'),
    d('ORT', 'Orthopaedics', 'clinical'),
    d('ENT', 'ENT', 'clinical'),
    d('EYE', 'Ophthalmology', 'clinical'),
    d('CAR', 'Cardiology', 'clinical'),
    d('DER', 'Dermatology', 'clinical'),
    d('PSY', 'Psychiatry', 'clinical'),
    d('NEU', 'Neurology', 'clinical'),
    d('URO', 'Urology', 'clinical'),
    d('GAS', 'Gastroenterology', 'clinical'),
];
const DOCTOR_FIELDS = ['isDoctor', 'acceptsAppointments', 'specialty', 'signature'];
const SEEDS = {
    core: {
        screens: ['/dashboard', '/people', '/admin/audit', '/admin/branches', '/admin/settings', '/admin/listing', '/support'],
        numberSeries: [],
        settings: [
            'displayName', 'logoUrl', 'primaryColor', 'letterheadHtml', 'letterhead', 'locale',
            'taxDocumentsSecondaryLanguage', 'clinicalDocumentsSecondaryLanguage',
            'sessionTimeoutMinutes', 'enforce2faForAdmins', 'ipAllowlist', 'numberPatterns',
        ],
        departments: [d('ADM', 'Administration', 'administrative')],
    },
    opd: {
        screens: ['/queue', '/appointments', '/schedules', '/follow-ups', '/online-requests', '/consultations'],
        numberSeries: ['token'],
        settings: [
            'freeFollowUpDays', 'reminderHours', 'requireCosignForMedicalOfficer', 'paidNoShowPolicy', 'followUpEngine', 'followUpValidDays',
            'walkInWithoutSession', 'screeningBeforeReady', 'queueOrder', 'portalMaxUpcomingPerPatient', 'quoteValidDays',
            'consultationChargeAt', 'payBeforeConsultation', 'tokenAfterPayment',
            'dayClosesAt', 'autoNoShow', 'autoNoShowEnabledAt', 'startWithoutArrival', 'backEntryDays', 'requirePrimaryDiagnosis',
        ],
        departments: CLINICAL_DEPARTMENTS,
        staffFields: DOCTOR_FIELDS,
        reportDatasets: ['encounters', 'appointments'],
        rawExport: [
            { table: 'appointments', label: 'Appointments', columns: ['id', 'patientId', 'doctorId', 'branchId', 'slotAt', 'status', 'type', 'channel', 'createdAt'] },
            { table: 'encounters', label: 'Consultations', columns: ['id', 'patientId', 'doctorId', 'branchId', 'context', 'status', 'createdAt'] },
        ],
    },
    ipd: {
        screens: ['/ipd/beds', '/ipd/admissions', '/ipd/locator', '/ipd/charge-rules', '/ipd/dues', '/ipd/wards'],
        numberSeries: ['admission', 'indent'],
        settings: ['requireCosignForMedicalOfficer', 'wardStockModel', 'staffAlertSmsEscalation', 'staffAlertSmsDelayMinutes', 'ipdDoseTimes', 'ipdMealTimes', 'ipdVitalsIntervalMinutes', 'ipdWardVitalsIntervalMinutes', 'ipdOverdueEscalationMinutes'],
        departments: [...CLINICAL_DEPARTMENTS.slice(0, 6), d('ICU', 'Intensive Care', 'clinical'), d('NUR', 'Nursing', 'support')],
        staffFields: DOCTOR_FIELDS,
        reportDatasets: ['admissions'],
        rawExport: [
            { table: 'admissions', label: 'Admissions', columns: ['id', 'number', 'patientId', 'branchId', 'admittedAt', 'dischargedAt', 'status', 'createdAt'] },
        ],
    },
    pharmacy: {
        screens: ['/pharmacy', '/online-orders', '/stock-control', '/payables'],
        numberSeries: ['grn', 'purchase_order', 'supplier_payment', 'purchase_return', 'stock_take', 'stock_adjustment', 'online_order'],
        settings: ['supplierPaymentApprovalLimitMinor', 'stockAdjustApprovalLimitMinor', 'posReturnApprovalLimitMinor'],
        departments: [d('PHR', 'Pharmacy', 'pharmacy')],
        reportDatasets: ['pharmacy_sales'],
    },
    billing: {
        screens: ['/billing', '/billing/shift', '/billing/refunds', '/billing/credit-customers', '/billing/charges', '/billing/settings'],
        numberSeries: ['invoice', 'receipt', 'estimate', 'credit_note'],
        settings: [
            'requireOpenShift', 'allowCashWithoutShift', 'requireTxnRef', 'blindCashCount', 'quoteValidDays',
            'vatNumber', 'sellerName', 'sellerNameAr', 'crNumber',
        ],
        departments: [d('BIL', 'Billing & Cashier', 'administrative')],
        reportDatasets: ['invoices', 'payments'],
        rawExport: [
            { table: 'invoices', label: 'Invoices', columns: ['id', 'number', 'patientId', 'branchId', 'status', 'netMinor', 'paidMinor', 'issuedAt', 'createdAt'] },
            { table: 'payments', label: 'Payments', columns: ['id', 'invoiceId', 'branchId', 'mode', 'amountMinor', 'receivedAt', 'createdAt'] },
        ],
    },
    lab: {
        screens: ['/lab', '/lab/critical', '/lab/home-collection', '/lab/qc', '/lab/catalogue', '/lab/reports', '/online-requests'],
        numberSeries: ['accession'],
        settings: [
            'labelWidthMm', 'labelHeightMm', 'labelCommandLang', 'printBridgePort', 'analyserResultsEditable',
            'criticalResultEscalationPhone', 'separateVerifier', 'qcWindowHours', 'criticalEscalationMinutes',
            'billOverlappingPanels', 'accessionPerOrder', 'releaseRequiresPayment', 'statCollectionMinutes',
            'reportReadySms', 'accessionMode', 'tubeSuffix', 'homeCollectionEnabled', 'collectionIdentityCheck',
            'collectRequiresPayment', 'analyserOverride', 'labelReprintApproval', 'labelShowDob', 'labelShowNationalId',
            'reportHeaderMode', 'preprintedTopMm', 'labReportLanguage', 'instrumentLogDays', 'publicPortalUrl',
        ],
        departments: [d('LAB', 'Laboratory', 'diagnostic')],
        staffFields: ['signature'],
        reportDatasets: ['lab_orders'],
        rawExport: [
            { table: 'lab_orders', label: 'Lab orders', columns: ['id', 'number', 'patientId', 'branchId', 'orderedAt', 'createdAt'] },
        ],
    },
    radiology: {
        screens: ['/radiology'],
        settings: ['radiologySeparateVerifier'],
        departments: [d('RAD', 'Radiology', 'diagnostic')],
        staffFields: DOCTOR_FIELDS,
        rawExport: [
            { table: 'radiology_studies', label: 'Imaging studies', columns: ['id', 'number', 'patientId', 'branchId', 'modality', 'status', 'orderedAt', 'createdAt'] },
        ],
    },
    er: {
        screens: ['/er'],
        numberSeries: ['er_case'],
        settings: ['requireCosignForMedicalOfficer'],
        departments: [d('EMR', 'Emergency', 'clinical')],
        staffFields: DOCTOR_FIELDS,
        rawExport: [{ table: 'er_cases', label: 'Emergency cases', columns: ['id', 'number', 'patientId', 'branchId', 'arrivedAt', 'disposition', 'createdAt'] }],
    },
    ot: {
        screens: ['/ot'],
        departments: [d('OT', 'Operation Theatre', 'clinical'), d('ANA', 'Anaesthesia', 'clinical')],
        staffFields: DOCTOR_FIELDS,
        rawExport: [{ table: 'ot_cases', label: 'Theatre cases', columns: ['id', 'patientId', 'branchId', 'procedureName', 'status', 'scheduledStart', 'createdAt'] }],
    },
    blood_bank: { screens: ['/blood-bank'], numberSeries: ['blood_donor', 'blood_unit'], departments: [d('BBK', 'Blood Bank', 'diagnostic')] },
    inventory: {
        screens: ['/inventory', '/stock-control', '/payables', '/assets'],
        numberSeries: ['grn', 'purchase_order', 'supplier_payment', 'purchase_return', 'stock_take', 'stock_adjustment', 'maintenance_ticket'],
        settings: ['supplierPaymentApprovalLimitMinor', 'stockAdjustApprovalLimitMinor'],
        departments: [d('STR', 'Stores', 'support')],
    },
    hr: { numberSeries: ['employee'], departments: [d('HRD', 'Human Resources', 'administrative')] },
    accounting: {
        screens: ['/accounting', '/accounting/payouts'],
        numberSeries: ['journal'],
        departments: [d('FIN', 'Finance & Accounts', 'administrative')],
    },
    insurance: {
        screens: ['/insurance'],
        numberSeries: ['claim', 'payer_invoice'],
        settings: ['insurancePreauthMode'],
        departments: [d('INS', 'Insurance & Panels', 'administrative')],
        reportDatasets: ['claims'],
        rawExport: [{ table: 'claims', label: 'Claims', columns: ['id', 'number', 'patientId', 'status', 'claimedAmountMinor', 'createdAt'] }],
    },
    portal: { settings: ['portalMaxUpcomingPerPatient'] },
    mrd: { screens: ['/mrd'], departments: [d('MRD', 'Medical Records', 'administrative')] },
    reports: { screens: ['/reports'] },
    notifications: { screens: ['/notifications'] },
    ambulance: { screens: ['/ambulance'], departments: [d('AMB', 'Ambulance', 'support')] },
    dietary: { screens: ['/dietary'], departments: [d('DIE', 'Dietary & Kitchen', 'support')] },
    housekeeping: { screens: ['/housekeeping'], departments: [d('HKP', 'Housekeeping', 'support')] },
    feedback: { screens: ['/feedback'] },
    tasks: { screens: ['/tasks'] },
    maternity: {
        screens: ['/maternity'],
        departments: [d('GYN', 'Gynaecology & Obstetrics', 'clinical'), d('LDR', 'Labour Room', 'clinical')],
        staffFields: DOCTOR_FIELDS,
    },
    dialysis: { screens: ['/therapy'], numberSeries: ['therapy'], departments: [d('DIA', 'Dialysis Unit', 'clinical')], staffFields: DOCTOR_FIELDS },
    daycare: { screens: ['/therapy'], numberSeries: ['therapy'], departments: [d('DAY', 'Day Care', 'clinical')], staffFields: DOCTOR_FIELDS },
    dental: { screens: ['/dental'], departments: [d('DEN', 'Dental', 'clinical')], staffFields: DOCTOR_FIELDS },
    nphies: {},
    wasfaty: {},
};
const PATIENT_SERIES_MODULES = ['opd', 'ipd', 'er', 'ot', 'lab', 'radiology', 'blood_bank', 'maternity', 'dialysis', 'daycare', 'dental', 'mrd'];
const ENCOUNTER_SERIES_MODULES = ['opd', 'ipd', 'er', 'ot', 'maternity', 'dialysis', 'daycare', 'dental'];
const ENCOUNTER_SERIES = ['prescription', 'certificate'];
const ADD_ON_SET = new Set(ADD_ON_MODULES);
export const MODULE_MANIFEST = Object.fromEntries(MODULE_CODES.map((code, index) => {
    const seed = SEEDS[code];
    const numberSeries = [
        ...(seed.numberSeries ?? []),
        ...(PATIENT_SERIES_MODULES.includes(code) ? ['mrn'] : []),
        ...(ENCOUNTER_SERIES_MODULES.includes(code) ? ENCOUNTER_SERIES : []),
    ];
    const manifest = {
        code,
        kind: code === 'core' ? 'core' : ADD_ON_SET.has(code) ? 'add_on' : 'module',
        label: MODULE_CATALOGUE.find((module) => module.code === code)?.name ?? code,
        roleGroupOrder: code === 'core' ? 0 : index,
        screens: seed.screens ?? [],
        numberSeries,
        settings: seed.settings ?? [],
        departments: seed.departments ?? [],
        staffFields: seed.staffFields ?? [],
        reportDatasets: seed.reportDatasets ?? [],
        rawExport: seed.rawExport ?? [],
    };
    return [code, manifest];
}));
function union(modules, pick) {
    const out = new Set();
    for (const module of modules) {
        const manifest = MODULE_MANIFEST[module];
        if (manifest)
            for (const value of pick(manifest))
                out.add(value);
    }
    return [...out];
}
export function permissionsOfModule(module) {
    return PERMISSIONS.filter((permission) => permission.modules.includes(module)).map((permission) => permission.code);
}
export function roleTemplatesOfModule(module) {
    return ROLE_TEMPLATES.filter((template) => (template.groupModule ?? 'core') === module);
}
export function seriesFor(modules) {
    return union(modules, (manifest) => manifest.numberSeries);
}
export function settingsFor(modules) {
    return union(modules, (manifest) => manifest.settings);
}
export function staffFieldsFor(modules) {
    return union(modules, (manifest) => manifest.staffFields);
}
export function screensFor(modules) {
    return union(modules, (manifest) => manifest.screens);
}
export function departmentsFor(modules) {
    const byCode = new Map();
    for (const department of union(['core', ...modules], (manifest) => manifest.departments)) {
        if (!byCode.has(department.code))
            byCode.set(department.code, department);
    }
    return [...byCode.values()];
}
export function modulesOfScreen(path) {
    return MODULE_CODES.filter((code) => MODULE_MANIFEST[code].screens.includes(path));
}
export function modulesOfSeries(series) {
    return MODULE_CODES.filter((code) => MODULE_MANIFEST[code].numberSeries.includes(series));
}
export function modulesOfSetting(key) {
    return MODULE_CODES.filter((code) => MODULE_MANIFEST[code].settings.includes(key));
}
export const STANDARD_SPECIALTIES = [
    { code: 'GENMED', name: 'General Medicine' },
    { code: 'FAMMED', name: 'Family Medicine / GP' },
    { code: 'CARDIO', name: 'Cardiology' },
    { code: 'GENSUR', name: 'General Surgery' },
    { code: 'OBGYN', name: 'Obstetrics & Gynaecology' },
    { code: 'PEDIA', name: 'Paediatrics' },
    { code: 'NEONAT', name: 'Neonatology' },
    { code: 'ORTHO', name: 'Orthopaedics' },
    { code: 'ENT', name: 'ENT' },
    { code: 'OPHTH', name: 'Ophthalmology' },
    { code: 'DERMA', name: 'Dermatology' },
    { code: 'PSYCH', name: 'Psychiatry' },
    { code: 'DENT', name: 'Dentistry' },
    { code: 'NEURO', name: 'Neurology' },
    { code: 'NEUSUR', name: 'Neurosurgery' },
    { code: 'URO', name: 'Urology' },
    { code: 'GASTRO', name: 'Gastroenterology' },
    { code: 'PULMO', name: 'Pulmonology' },
    { code: 'NEPHRO', name: 'Nephrology' },
    { code: 'ENDO', name: 'Endocrinology' },
    { code: 'ONCO', name: 'Oncology' },
    { code: 'HAEM', name: 'Haematology' },
    { code: 'RHEUM', name: 'Rheumatology' },
    { code: 'INFDIS', name: 'Infectious Diseases' },
    { code: 'RADIO', name: 'Radiology' },
    { code: 'PATH', name: 'Pathology' },
    { code: 'ANAES', name: 'Anaesthesia' },
    { code: 'EMERG', name: 'Emergency Medicine' },
    { code: 'ICU', name: 'Critical Care' },
    { code: 'PLASUR', name: 'Plastic Surgery' },
    { code: 'CARSUR', name: 'Cardiothoracic Surgery' },
    { code: 'PHYSIO', name: 'Physiotherapy & Rehabilitation' },
];
export const BRANCH_CATEGORY_MODULES = [
    'opd', 'ipd', 'er', 'ot', 'lab', 'radiology', 'pharmacy', 'blood_bank', 'maternity', 'dialysis', 'daycare', 'dental',
];
//# sourceMappingURL=manifest.js.map